import * as React from "react";
import { Camera, Type, Loader2, ArrowLeft, Upload, MapPin, X } from "lucide-react";
import { Modal } from "@/components/Modal.js";
import { SearchableSelect, type PlacementSuggestionResult } from "@/components/SearchableSelect.js";
import { TagInput } from "@/components/TagInput.js";
import { apiFetch } from "@/lib/api.js";
import { parseItemFile } from "@/lib/parseItemFile.js";
import { uploadPhoto } from "@/lib/uploadPhoto.js";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext.js";
import { usePlan } from "@/contexts/PlanContext.js";
import { useToast } from "@/components/Toast.js";
import { useSettings } from "@/contexts/useSettings.js";
import type { Space } from "@/hooks/useSpaces.js";

type View = "photo" | "text" | "bulk";

interface Props {
  open: boolean;
  onClose: () => void;
  spaces: Space[];
  /** Called after a successful save. `nudge` is populated only for a single
   *  text-added item that was created WITHOUT a space, so the caller can offer
   *  a lightweight "Find a home for this?" prompt (F-a). */
  onSaved: (nudge?: { itemId: string; needsHome: boolean }) => void;
  initialTab?: "photo" | "text";
  initialSpaceId?: string;
  onSpaceCreated?: () => void;
}

export function AddItemModal({ open, onClose, spaces, onSaved, initialTab, initialSpaceId, onSpaceCreated }: Props) {
  const { aiEnabled } = useFeatureFlags();
  const { usage, refresh: refreshPlan } = usePlan();
  const { quickAddFields } = useSettings();
  const toast = useToast();
  const [view, setView] = React.useState<View>(initialTab ?? (aiEnabled ? "photo" : "text"));

  // Sticky space — persists across Add More and modal reopens
  const stickySpaceRef = React.useRef<string | null>(null);

  // Shared fields
  const [name, setName] = React.useState("");
  const [spaceId, setSpaceId] = React.useState<string | null>(null);
  const [quantity, setQuantity] = React.useState(1);
  const [description, setDescription] = React.useState("");
  const [tags, setTags] = React.useState<string[]>([]);
  const [status, setStatus] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [manufacturer, setManufacturer] = React.useState("");
  const [purchaseDate, setPurchaseDate] = React.useState("");
  const [careInstructions, setCareInstructions] = React.useState("");
  const [lastUsedDate, setLastUsedDate] = React.useState("");
  const [estimatedValue, setEstimatedValue] = React.useState<number | null>(null);
  const [allTags, setAllTags] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  // Photo flow
  const [photoPreview, setPhotoPreview] = React.useState<string | null>(null);
  const [photoMode, setPhotoMode] = React.useState<"single" | "multi" | null>(null);
  const [multiItems, setMultiItems] = React.useState<any[]>([]);
  const [photoKey, setPhotoKey] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);

  // Bulk flow
  const [bulkText, setBulkText] = React.useState("");
  const [parsedItems, setParsedItems] = React.useState<any[]>([]);

  // Duplicate detection (checked on submit)
  const [dupWarning, setDupWarning] = React.useState<{ existingId: string; existingQuantity: number } | null>(null);
  const dupConfirmedRef = React.useRef(false);
  React.useEffect(() => { setDupWarning(null); dupConfirmedRef.current = false; }, [name, spaceId]);

  const quickAddFieldsRef = React.useRef(quickAddFields);
  quickAddFieldsRef.current = quickAddFields;

  // Flow #3 — "Place a new item". Triggered from *inside* the location picker
  // (not a standalone button) so it never disrupts the default flow of picking
  // an existing location. Only wired up when AI is enabled and a name is set.
  const requestPlacementSuggestion = React.useCallback(async (): Promise<PlacementSuggestionResult | null> => {
    if (!name.trim()) return null;
    const res = await apiFetch<PlacementSuggestionResult>("/api/ai/place-item", {
      method: "POST",
      body: JSON.stringify({ name: name.trim(), tags, description: description.trim() || undefined }),
    });
    refreshPlan();
    return res;
  }, [name, tags, description, refreshPlan]);

  React.useEffect(() => {
    if (open) {
      setView(initialTab ?? (aiEnabled ? "photo" : "text"));
      setSpaceId(initialSpaceId ?? stickySpaceRef.current);
      if (initialSpaceId) stickySpaceRef.current = initialSpaceId;
      apiFetch<{ tags: { name: string; count: number }[]; suggestions: { name: string; count: number }[] }>("/api/tags").then((r) => setAllTags([...r.tags, ...r.suggestions].map((x) => x.name))).catch(() => {});
    }
  }, [open, initialTab, initialSpaceId]);

  // Keep sticky refs in sync
  React.useEffect(() => { stickySpaceRef.current = spaceId; }, [spaceId]);

  const reset = () => {
    setName(""); setQuantity(1); setDescription(""); setTags([]);
    setStatus(""); setNotes(""); setManufacturer(""); setPurchaseDate("");
    setCareInstructions(""); setLastUsedDate(""); setEstimatedValue(null);
    setPhotoPreview(null); setPhotoMode(null); setMultiItems([]); setPhotoKey(null); setUploading(false);
    setBulkText(""); setParsedItems([]); setError("");
    setDupWarning(null); dupConfirmedRef.current = false;
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSaveSingle = async (addMore = false) => {
    if (!name.trim()) { setError("Item name is required"); return; }
    setLoading(true); setError("");
    try {
      // Check for duplicates on first submit; skip if user already confirmed
      if (!dupConfirmedRef.current) {
        const p = new URLSearchParams({ name: name.trim() });
        if (spaceId) p.set("spaceId", String(spaceId));
        const res = await apiFetch<{ exists: boolean; existingId: string | null; existingQuantity: number | null }>(`/api/items/check-duplicate?${p}`);
        if (res.exists) {
          setDupWarning({ existingId: res.existingId!, existingQuantity: res.existingQuantity! });
          dupConfirmedRef.current = true;
          setLoading(false);
          return;
        }
      }
      const body: Record<string, unknown> = { name: name.trim(), spaceId, quantity: quickAddFields.includes("quantity") ? quantity : 1, description: description.trim() || null, tags };
      if (photoKey) body.photoUrl = photoKey;
      if (quickAddFields.includes("status") && status) body.status = status;
      if (quickAddFields.includes("notes") && notes.trim()) body.notes = notes.trim();
      if (quickAddFields.includes("manufacturer") && manufacturer.trim()) body.manufacturer = manufacturer.trim();
      if (quickAddFields.includes("purchaseDate") && purchaseDate) body.purchaseDate = purchaseDate;
      if (quickAddFields.includes("careInstructions") && careInstructions.trim()) body.careInstructions = careInstructions.trim();
      if (quickAddFields.includes("lastUsedDate") && lastUsedDate) body.lastUsedDate = lastUsedDate;
      if (quickAddFields.includes("estimatedValue") && estimatedValue != null) body.estimatedValue = estimatedValue;
      const created = await apiFetch<{ id: string }>("/api/items", { method: "POST", body: JSON.stringify(body) });
      // F-a: only nudge when the item was created without a home, and only on a
      // committing save (not "add more" bulk-adding, which should stay fast).
      onSaved(!spaceId && !addMore && created?.id ? { itemId: created.id, needsHome: true } : undefined);
      if (addMore) { setName(""); setQuantity(1); setDescription(""); setTags([]); setStatus(""); setNotes(""); setManufacturer(""); setPurchaseDate(""); setCareInstructions(""); setLastUsedDate(""); setEstimatedValue(null); }
      else handleClose();
    } catch (e: any) { setError(e.message ?? "Failed to save item"); }
    setLoading(false);
  };

  // --- Photo flow ---
  const handlePhotoCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = (reader.result as string).split(",")[1];
      setPhotoPreview(reader.result as string);
      setLoading(true); setError("");
      try {
        const canUpload = !usage || usage.maxPhotos === -1 || usage.photosUsed < usage.maxPhotos;
        const [result] = await Promise.all([
          apiFetch<{ mode: "single" | "multi"; item?: any; items?: any[] }>("/api/ai/identify-auto", { method: "POST", body: JSON.stringify({ imageBase64: base64, mimeType: file.type }) }),
          canUpload ? uploadPhoto(file, { storeOriginal: usage?.storeOriginal ?? false }).then((r) => setPhotoKey(r.displayKey)).catch(() => {}) : Promise.resolve(),
        ]);
        refreshPlan();
        setPhotoMode(result.mode);
        if (result.mode === "single" && result.item) {
          setName(result.item.name || ""); setDescription(result.item.description || "");
          setQuantity(result.item.quantity || 1); setTags(result.item.tags || []);
        } else {
          setMultiItems(result.items || []);
        }
      } catch (err: any) { setError(err.message); }
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveMultiItems = async () => {
    if (!multiItems.length) return;
    setLoading(true); setError("");
    try {
      await apiFetch("/api/items/bulk", { method: "POST", body: JSON.stringify({ items: multiItems.map((i) => ({ name: i.name, quantity: i.quantity || 1, description: i.description || null, spaceId, tags: i.tags || [], photoUrl: photoKey || undefined })) }) });
      onSaved(); handleClose();
    } catch (e: any) { setError(e.message ?? "Failed to save items"); }
    setLoading(false);
  };

  // --- Bulk flow ---
  const handleBulkSave = async () => {
    const names = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!names.length) return;
    setLoading(true); setError("");
    try {
      await apiFetch("/api/items/bulk", { method: "POST", body: JSON.stringify({ items: names.map((n) => ({ name: n, spaceId, tags })) }) });
      onSaved(); handleClose();
    } catch (e: any) { setError(e.message ?? "Failed to save items"); }
    setLoading(false);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true); setError("");

    if (file.type.startsWith("image/")) {
      // Image → AI OCR
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = (reader.result as string).split(",")[1];
          const result = await apiFetch<{ items: any[] }>("/api/ai/parse-bulk", { method: "POST", body: JSON.stringify({ content: base64, mimeType: file.type }) });
          setParsedItems(result.items || []);
        } catch (err: any) { setError(err.message); }
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } else {
      // Text/CSV → local parse first, AI fallback
      const reader = new FileReader();
      reader.onload = async () => {
        const text = reader.result as string;
        const parsed = parseItemFile(text, file.name);
        if (parsed.valid && parsed.items.length > 0) {
          setParsedItems(parsed.items);
          setLoading(false);
        } else {
          try {
            const result = await apiFetch<{ items: any[] }>("/api/ai/parse-bulk", { method: "POST", body: JSON.stringify({ content: text }) });
            setParsedItems(result.items || []);
          } catch (err: any) { setError(err.message); }
          setLoading(false);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleSaveParsedItems = async () => {
    if (!parsedItems.length) return;
    setLoading(true); setError("");
    try {
      await apiFetch("/api/items/bulk", { method: "POST", body: JSON.stringify({ items: parsedItems.map((i) => ({ name: i.name, quantity: i.quantity || 1, description: i.description || null, spaceId, tags })) }) });
      onSaved(); handleClose();
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  };

  const handleTextPhotoAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const { displayKey } = await uploadPhoto(file, { storeOriginal: usage?.storeOriginal ?? false });
      setPhotoKey(displayKey);
    } catch { setError("Photo upload failed"); setPhotoPreview(null); }
    setUploading(false);
  };

  const photoQuotaReached = usage ? usage.maxPhotos !== -1 && usage.photosUsed >= usage.maxPhotos : false;

  const fieldInput = "w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary";

  const optionalFields = (
    <>
      {quickAddFields.includes("status") && (
        <div>
          <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Status</label>
          <input placeholder="e.g. in use, stored, lent out" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" className={fieldInput} />
        </div>
      )}
      {quickAddFields.includes("notes") && (
        <div>
          <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Notes</label>
          <textarea placeholder="Additional notes..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} aria-label="Notes" className={fieldInput + " resize-none"} />
        </div>
      )}
      {quickAddFields.includes("manufacturer") && (
        <div>
          <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Manufacturer</label>
          <input placeholder="Brand or maker" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} aria-label="Manufacturer" className={fieldInput} />
        </div>
      )}
      {quickAddFields.includes("purchaseDate") && (
        <div>
          <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Purchase Date</label>
          <input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} aria-label="Purchase date" className={fieldInput} />
        </div>
      )}
      {quickAddFields.includes("careInstructions") && (
        <div>
          <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Care Instructions</label>
          <textarea placeholder="Cleaning or maintenance tips..." value={careInstructions} onChange={(e) => setCareInstructions(e.target.value)} rows={2} aria-label="Care instructions" className={fieldInput + " resize-none"} />
        </div>
      )}
      {quickAddFields.includes("lastUsedDate") && (
        <div>
          <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Last Used Date</label>
          <input type="date" value={lastUsedDate} onChange={(e) => setLastUsedDate(e.target.value)} aria-label="Last used date" className={fieldInput} />
        </div>
      )}
      {quickAddFields.includes("estimatedValue") && (
        <div>
          <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Estimated Value ($)</label>
          <input type="number" min={0} step="0.01" placeholder="0.00" value={estimatedValue ?? ""} onChange={(e) => setEstimatedValue(e.target.value ? Math.round(Number(e.target.value) * 100) : null)} aria-label="Estimated value in dollars" className={fieldInput} />
        </div>
      )}
    </>
  );

  return (
    <Modal open={open} onClose={handleClose} wide>
      {/* Sub-view header with back button */}
      <div className="flex items-center gap-2 mb-4 -mt-1">
        {view !== "photo" && (
          <button onClick={() => { reset(); setView(aiEnabled ? "photo" : "text"); }} aria-label="Back" className="p-1 rounded-md text-faint hover:bg-amethyst-soft hover:text-amethyst-ink transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <h2 className="font-headline text-xl font-semibold text-ink tracking-[-0.01em]">
          {view === "photo" && "Add Item"}
          {view === "text" && "Add Manually"}
          {view === "bulk" && "Add Many"}
        </h2>
      </div>

      {error && (
        <div className="text-xs mb-3">
          {error.includes("limit") ? (
            <p className="text-warning-text">AI action limit reached. <a href="/settings" className="underline font-medium">Upgrade your plan</a> for more.</p>
          ) : (
            <p className="text-error-text">{error}</p>
          )}
        </div>
      )}

      {/* Sticky location context bar */}
      {spaceId != null && spaceId !== "" && view === "photo" && (() => {
        const space = spaces.find((s) => s.id === spaceId);
        return space ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amethyst-soft text-[0.8125rem] text-amethyst-ink mb-1">
            <MapPin className="w-3.5 h-3.5" />
            <span className="flex-1">Adding to: {space.name}</span>
            <button onClick={() => { setSpaceId(null); stickySpaceRef.current = null; }} aria-label="Clear location" className="p-0.5 rounded-sm hover:bg-amethyst/20 transition-colors"><X className="w-3 h-3" /></button>
          </div>
        ) : null;
      })()}

      {/* ===== PHOTO VIEW (default entry) ===== */}
      {view === "photo" && (
        <div className="space-y-3">
          {!photoPreview && !loading && (
            <>
              <label className="flex flex-col items-center gap-3 p-8 rounded-lg border-2 border-dashed border-clay-border cursor-pointer hover:border-sage-deep/50 hover:bg-sage-wash/40 transition-colors">
                <Camera className="w-10 h-10 text-sage-deep" />
                <span className="text-sm font-medium text-body">Snap a photo</span>
                <span className="text-[0.8125rem] text-faint text-center max-w-xs">Point at one item or a whole shelf — we'll identify and add them for you</span>
                <input type="file" accept="image/*" capture="environment" onChange={handlePhotoCapture} className="hidden" />
              </label>
              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 h-px bg-clay-border" />
                <span className="text-[0.8125rem] text-faint">or</span>
                <div className="flex-1 h-px bg-clay-border" />
              </div>
              <div className="flex gap-3">
                <button onClick={() => { reset(); setView("text"); }} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md border border-clay-border text-sm text-body font-medium hover:border-primary/40 hover:text-primary transition-colors">
                  <Type className="w-4 h-4" />Type manually
                </button>
                <button onClick={() => { reset(); setView("bulk"); }} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md border border-clay-border text-sm text-body font-medium hover:border-primary/40 hover:text-primary transition-colors">
                  <Upload className="w-4 h-4" />Import a list
                </button>
              </div>
            </>
          )}

          {photoPreview && (
            <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
          )}

          {loading && (
            <div className="flex items-center gap-2 text-sm text-faint"><Loader2 className="w-4 h-4 animate-spin text-sage-deep" />Identifying...</div>
          )}

          {/* Single item result */}
          {photoPreview && !loading && photoMode === "single" && (
            <>
              <input placeholder="Item name" value={name} onChange={(e) => setName(e.target.value)} aria-label="Item name" className={fieldInput} />
              {dupWarning && (
                <p className="text-[0.8125rem] text-warning-text bg-warning-wash px-3 py-2 rounded-md">
                  "{name.trim()}" already exists{spaceId ? " in this space" : ""} (qty: {dupWarning.existingQuantity}).{" "}
                  <a href={`/items/${dupWarning.existingId}`} className="underline font-medium" target="_blank" rel="noopener noreferrer">View existing</a> or save again to add anyway.
                </p>
              )}
              <SearchableSelect spaces={spaces} value={spaceId} onChange={setSpaceId} onSpaceCreated={onSpaceCreated} />
              <input type="number" min={0} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} className={fieldInput + " tabular-nums"} />
              <textarea placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={fieldInput + " resize-none"} />
              <TagInput tags={tags} onChange={setTags} suggestions={allTags} />
              {optionalFields}
              <button onClick={() => handleSaveSingle()} disabled={!name.trim()} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">Add to Inventory</button>
            </>
          )}

          {/* Multi item result */}
          {photoPreview && !loading && photoMode === "multi" && (
            <>
              <p className="text-sm font-medium">{multiItems.length} items found</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {multiItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-md bg-paper-raised border border-clay-border text-sm">
                    <input
                      value={item.name}
                      onChange={(e) => {
                        const updated = [...multiItems];
                        updated[i] = { ...updated[i], name: e.target.value };
                        setMultiItems(updated);
                      }}
                      className="flex-1 bg-transparent border-b border-transparent text-body hover:border-clay-border focus:border-primary focus:outline-none px-1 py-0.5 text-sm"
                      aria-label={`Edit item ${i + 1} name`}
                    />
                    {item.quantity > 1 && <span className="text-[0.8125rem] text-faint shrink-0 tabular-nums">x{item.quantity}</span>}
                    <button onClick={() => setMultiItems(multiItems.filter((_, j) => j !== i))} className="text-[0.8125rem] text-error-text hover:text-error shrink-0">Remove</button>
                  </div>
                ))}
              </div>
              <SearchableSelect spaces={spaces} value={spaceId} onChange={setSpaceId} onSpaceCreated={onSpaceCreated} />
              <button onClick={handleSaveMultiItems} disabled={loading} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">Add {multiItems.length} Items</button>
            </>
          )}
        </div>
      )}

      {/* ===== TEXT VIEW (Single Item) ===== */}
      {view === "text" && (
        <div className="space-y-3">
          <div>
            <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Item Name</label>
            <input placeholder="Enter item name" value={name} onChange={(e) => setName(e.target.value)} aria-label="Item name" className={fieldInput} />
          </div>
          {/* Photo attachment */}
          {photoKey && photoPreview ? (
            <div className="relative w-16 h-16">
              <img src={photoPreview} alt="" className="w-16 h-16 rounded-md object-cover" />
              <button onClick={() => { setPhotoKey(null); setPhotoPreview(null); }} aria-label="Remove photo" className="absolute -top-1 -right-1 w-5 h-5 rounded-pill bg-error text-white text-xs flex items-center justify-center">×</button>
            </div>
          ) : uploading ? (
            <div className="flex items-center gap-2 text-[0.8125rem] text-faint"><Loader2 className="w-3 h-3 animate-spin" />Uploading photo...</div>
          ) : (
            <label className={`inline-flex items-center gap-1.5 text-[0.8125rem] cursor-pointer ${photoQuotaReached ? "text-faint cursor-not-allowed" : "text-primary hover:underline"}`}>
              <Camera className="w-3.5 h-3.5" />{photoQuotaReached ? `Photo limit reached (${usage?.maxPhotos})` : "Add photo"}
              {!photoQuotaReached && <input type="file" accept="image/*" onChange={handleTextPhotoAttach} className="hidden" />}
            </label>
          )}
          {dupWarning && (
            <p className="text-[0.8125rem] text-warning-text bg-warning-wash px-3 py-2 rounded-md">
              "{name.trim()}" already exists{spaceId ? " in this space" : ""} (qty: {dupWarning.existingQuantity}).{" "}
              <a href={`/items/${dupWarning.existingId}`} className="underline font-medium" target="_blank" rel="noopener noreferrer">View existing</a> or save again to add anyway.
            </p>
          )}
          <div>
            <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Location</label>
            <SearchableSelect
              spaces={spaces}
              value={spaceId}
              onChange={setSpaceId}
              onSpaceCreated={onSpaceCreated}
              onRequestSuggestion={aiEnabled && name.trim() ? requestPlacementSuggestion : undefined}
            />
          </div>
          {quickAddFields.includes("quantity") && (
            <div>
              <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Quantity</label>
              <input type="number" min={0} placeholder="1" value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} className={fieldInput + " tabular-nums"} />
            </div>
          )}
          <div>
            <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Description <span className="text-faint normal-case font-normal">(optional)</span></label>
            <textarea placeholder="Add more details..." value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={fieldInput + " resize-none"} />
          </div>
          <div>
            <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Tags</label>
            <TagInput tags={tags} onChange={setTags} suggestions={allTags} />
            <p className="text-[0.8125rem] text-faint mt-1">Custom labels like "fragile" or "lent to Sam"</p>
          </div>
          {optionalFields}
          <div className="flex gap-2">
            <button onClick={() => handleSaveSingle()} disabled={loading || !name.trim()} className="flex-1 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Add to Inventory"}
            </button>
            <button onClick={() => handleSaveSingle(true)} disabled={loading || !name.trim()} className="px-4 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium hover:bg-amethyst-soft/70 transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
              Add More
            </button>
          </div>
        </div>
      )}

      {/* ===== BULK VIEW (Add Many) ===== */}
      {view === "bulk" && (
        <div className="space-y-3">
          {parsedItems.length === 0 ? (
            <>
              <textarea placeholder={"One item per line...\nChair\nLamp\nRug"} value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6} className={fieldInput + " resize-none"} />
              <label className="inline-flex items-center gap-1.5 text-[0.8125rem] text-primary cursor-pointer hover:underline">
                <Upload className="w-3.5 h-3.5" />Upload file (.txt, .csv, or image)
                <input type="file" accept=".txt,.csv,image/*" onChange={handleFileUpload} className="hidden" />
              </label>
              <SearchableSelect spaces={spaces} value={spaceId} onChange={setSpaceId} onSpaceCreated={onSpaceCreated} />
              <TagInput tags={tags} onChange={setTags} suggestions={allTags} />
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-faint"><Loader2 className="w-4 h-4 animate-spin" />Processing file...</div>
              ) : (
                <button onClick={handleBulkSave} disabled={!bulkText.trim()} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
                  Create {bulkText.split("\n").filter((l) => l.trim()).length} Items
                </button>
              )}
            </>
          ) : (
            <>
              <p className="text-sm font-medium">{parsedItems.length} items parsed</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {parsedItems.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-md bg-paper-raised border border-clay-border text-sm text-body">
                    <span>{item.name} {item.quantity > 1 && <span className="tabular-nums">x{item.quantity}</span>}</span>
                    <button onClick={() => setParsedItems(parsedItems.filter((_, j) => j !== i))} className="text-[0.8125rem] text-error-text hover:text-error">Remove</button>
                  </div>
                ))}
              </div>
              <SearchableSelect spaces={spaces} value={spaceId} onChange={setSpaceId} onSpaceCreated={onSpaceCreated} />
              <TagInput tags={tags} onChange={setTags} suggestions={allTags} />
              <div className="flex gap-2">
                <button onClick={handleSaveParsedItems} disabled={loading} className="flex-1 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : `Add ${parsedItems.length} Items`}
                </button>
                <button onClick={() => setParsedItems([])} className="px-4 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium hover:bg-amethyst-soft/70 transition-colors">Clear</button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
